import { useEffect, useState } from "react";
import { Plus, Edit3, Trash2, Calendar, EyeOff, Eye, Star } from "lucide-react";
import { toast } from "sonner";
import { adminListProperties, adminDeleteProperty, adminUpdateProperty } from "../../lib/api";
import PropertyEditor, { emptyProperty } from "./PropertyEditor";
import DatesEditor from "./DatesEditor";

export default function PropertiesPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [datesEditing, setDatesEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | active | inactive | featured

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await adminListProperties();
      setItems(data);
    } catch {
      toast.error("Could not load properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this residence? This cannot be undone.")) return;
    try { await adminDeleteProperty(id); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  const toggleActive = async (p) => {
    try {
      await adminUpdateProperty(p.id, { published: !p.published });
      toast.success(!p.published ? "Now visible on the public site" : "Hidden from the public site");
      refresh();
    } catch { toast.error("Update failed"); }
  };

  const filtered = items.filter((p) => {
    if (filter === "active") return p.published !== false;
    if (filter === "inactive") return p.published === false;
    if (filter === "featured") return !!p.featured;
    return true;
  });

  const counts = {
    all: items.length,
    active: items.filter((p) => p.published !== false).length,
    inactive: items.filter((p) => p.published === false).length,
    featured: items.filter((p) => !!p.featured).length,
  };

  return (
    <div data-testid="properties-panel">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="ed-overline mb-2">Operations</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">Properties</h1>
          <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5">
            {counts.all} residence{counts.all !== 1 ? "s" : ""} · {counts.active} active · {counts.inactive} hidden
          </p>
        </div>
        <button
          data-testid="admin-new-property"
          onClick={() => setEditing({ ...emptyProperty })}
          className="text-[0.65rem] uppercase tracking-[0.22em] px-5 py-3 bg-[var(--ed-emerald)] text-white hover:bg-[var(--ed-emerald-deep)] rounded-sm flex items-center gap-2"
        >
          <Plus size={13} /> New residence
        </button>
      </header>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2 mb-7 pb-5 border-b border-[var(--ed-rule)]">
        {[
          ["all", `All · ${counts.all}`],
          ["active", `Active · ${counts.active}`],
          ["inactive", `Hidden · ${counts.inactive}`],
          ["featured", `Featured · ${counts.featured}`],
        ].map(([k, l]) => (
          <button
            key={k}
            data-testid={`prop-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`text-[0.65rem] uppercase tracking-[0.22em] px-4 py-2 border rounded-sm transition-colors ${
              filter === k
                ? "bg-[var(--ed-ink)] text-[var(--ed-bg)] border-[var(--ed-ink)]"
                : "border-[var(--ed-rule)] text-[var(--ed-ink-soft)] hover:border-[var(--ed-ink)] hover:text-[var(--ed-ink)]"
            }`}
          >{l}</button>
        ))}
      </div>

      {loading ? (
        <p className="text-[var(--ed-ink-muted)] py-16 text-center">Loading residences…</p>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[var(--ed-rule)] rounded-sm p-16 text-center">
          <p className="text-[var(--ed-ink-muted)]">No properties match this view.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((p) => {
            const inactive = p.published === false;
            return (
              <div
                key={p.id}
                data-testid={`admin-prop-${p.id}`}
                className={`bg-[var(--ed-bg)] border border-[var(--ed-rule)] rounded-sm overflow-hidden flex flex-col transition-opacity ${
                  inactive ? "opacity-60" : ""
                }`}
              >
                <div className="aspect-[4/3] relative bg-[var(--ed-paper)]">
                  <img
                    src={p.images?.[0] || "https://placehold.co/600x400/EDE6D5/14110C?text=No+Image"}
                    alt={p.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
                    {inactive && (
                      <span className="bg-[var(--ed-ink)] text-[var(--ed-bg)] text-[0.55rem] tracking-[0.22em] uppercase px-2 py-1">
                        Hidden
                      </span>
                    )}
                    {p.featured && (
                      <span className="bg-[var(--ed-emerald)] text-white text-[0.55rem] tracking-[0.22em] uppercase px-2 py-1 flex items-center gap-1">
                        <Star size={9} /> Featured
                      </span>
                    )}
                    {p.bookable && (
                      <span className="bg-white/95 text-[var(--ed-ink)] text-[0.55rem] tracking-[0.22em] uppercase px-2 py-1">
                        Bookable
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-serif text-lg text-[var(--ed-ink)] leading-tight">{p.name}</h3>
                  <p className="text-[var(--ed-ink-muted)] text-xs mt-1">
                    {p.location} · £{p.price_per_night}/night · {p.bedrooms} bd · {p.bathrooms} ba
                  </p>
                  <p className="text-[var(--ed-ink-muted)] text-[0.7rem] mt-2 mb-4">
                    {p.blocked_dates?.length || 0} blocked date{p.blocked_dates?.length === 1 ? "" : "s"}
                  </p>
                  <div className="mt-auto flex flex-wrap gap-1.5">
                    <button
                      data-testid={`admin-edit-${p.id}`}
                      onClick={() => setEditing(p)}
                      className="text-[0.6rem] uppercase tracking-[0.2em] px-3 py-2 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] text-[var(--ed-ink-soft)] hover:text-[var(--ed-ink)] flex items-center gap-1.5 rounded-sm"
                    >
                      <Edit3 size={11} /> Edit
                    </button>
                    <button
                      data-testid={`admin-dates-${p.id}`}
                      onClick={() => setDatesEditing(p)}
                      className="text-[0.6rem] uppercase tracking-[0.2em] px-3 py-2 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] text-[var(--ed-ink-soft)] hover:text-[var(--ed-ink)] flex items-center gap-1.5 rounded-sm"
                    >
                      <Calendar size={11} /> Dates
                    </button>
                    <button
                      data-testid={`admin-toggle-${p.id}`}
                      onClick={() => toggleActive(p)}
                      className="text-[0.6rem] uppercase tracking-[0.2em] px-3 py-2 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] text-[var(--ed-ink-soft)] hover:text-[var(--ed-ink)] flex items-center gap-1.5 rounded-sm"
                    >
                      {inactive ? <Eye size={11} /> : <EyeOff size={11} />}
                      {inactive ? "Publish" : "Hide"}
                    </button>
                    <button
                      data-testid={`admin-delete-${p.id}`}
                      onClick={() => onDelete(p.id)}
                      className="text-[0.6rem] uppercase tracking-[0.2em] px-3 py-2 border border-red-200 hover:border-red-500 text-red-500 hover:bg-red-50 flex items-center gap-1.5 rounded-sm"
                      aria-label="Delete"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <PropertyEditor
          property={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
      {datesEditing && (
        <DatesEditor
          property={datesEditing}
          onClose={() => setDatesEditing(null)}
          onSaved={() => { setDatesEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}
